import React, { useState } from 'react';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { ORG_TYPES, isOrgType, orgTypeLabel } from '../utils/orgTypes';

const BatchUpload = ({ onBatchProcess, isProcessing = false }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadData, setUploadData] = useState(null);
  const [processingStatus, setProcessingStatus] = useState('');
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [batchResults, setBatchResults] = useState(null);
  const [defaultPassword, setDefaultPassword] = useState('bdfhFUif7324');
  const [autoRegisterUsers, setAutoRegisterUsers] = useState(true);

  // Get region options from environment variables
  const regionCodes = process.env.REACT_APP_REGIONS?.split(',') || ['US', 'EU', 'APAC'];
  const regionLabels = process.env.REACT_APP_REGION_LABELS?.split(',') || ['United States', 'Europe', 'Asia Pacific'];
  const currentRegionCode = process.env.REACT_APP_REGION_CODE || 'US';
  
  const regionOptions = regionCodes.map((code, index) => ({
    value: code,
    label: `${regionLabels[index] || code} (${code})`
  }));

  // Set default region on component mount
  React.useEffect(() => {
    if (!selectedRegion && regionOptions.length > 0) {
      const defaultRegion = regionOptions.find(r => r.value === currentRegionCode) || regionOptions[0];
      setSelectedRegion(defaultRegion);
    }
  }, []);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setSelectedFile(file);
      setUploadData(null);
      setProcessingStatus('');
    } else {
      toast.error('Please select a valid CSV file');
      event.target.value = '';
    }
  };

  const parseCSV = (csvText) => {
    try {
      const lines = csvText.split('\n').map(line => line.trim()).filter(line => line);
      
      if (lines.length === 0) {
        throw new Error('CSV file is empty');
      }
      
      // Parse organization details from header section
      const organizationData = {};
      const userData = [];
      
      let currentSection = 'org';
      let headersParsed = false;
      let userHeaders = [];
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        
        // Handle CSV parsing more robustly
        const cells = [];
        let current = '';
        let inQuotes = false;
        
        for (let j = 0; j < line.length; j++) {
          const char = line[j];
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            cells.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        cells.push(current.trim());
        
        // Remove quotes from cells
        for (let k = 0; k < cells.length; k++) {
          cells[k] = cells[k].replace(/^"/, '').replace(/"$/, '');
        }
        
        // Skip empty lines
        if (cells.length === 0 || (cells.length === 1 && cells[0] === '')) continue;
      
      // Parse organization section
      if (currentSection === 'org') {
        if (cells[0].toLowerCase().includes('company')) {
          organizationData.name = cells[1];
        } else if (cells[0].toLowerCase().includes('start date')) {
          organizationData.startDate = cells[1];
        } else if (cells[0].toLowerCase().includes('total seats') || cells[0].toLowerCase().includes('# of total seats')) {
          organizationData.maxUsers = parseInt(cells[1]) || 0;
        } else if (cells[0].toLowerCase().includes('contact name')) {
          organizationData.contactName = cells[1];
        } else if (cells[0].toLowerCase().includes('contact email')) {
          organizationData.contactEmail = cells[1];
        } else if (cells[0].toLowerCase().includes('contact phone')) {
          organizationData.contactPhone = cells[1] || '';
        } else if (cells[0].toLowerCase().includes('organization type')) {
          organizationData.orgType = (cells[1] || '').trim().toLowerCase();
        } else if (cells[0].toLowerCase() === 'name' && cells[1].toLowerCase() === 'email') {
          // Found user section headers
          currentSection = 'users';
          userHeaders = cells;
          headersParsed = true;
          continue;
        }
      }
      
      // Parse user section
      if (currentSection === 'users' && headersParsed) {
        if (cells.length >= 4) {
          const user = {
            name: cells[0],
            email: cells[1],
            accountLevel: parseInt(cells[2]) || 0,
            region: cells[3] || 'US'
          };
          
          // Convert account level to role
          user.role = user.accountLevel === 1 ? 'leader' : 'trainee';
          
          userData.push(user);
        }
      }
    }
    
      // Always include all regions for organization
      organizationData.regions = ['US', 'EU', 'APAC'];
      
      // Validate parsed data
      if (!organizationData.name) {
        throw new Error('Organization name not found in CSV file');
      }
      if (!organizationData.contactName) {
        throw new Error('Contact name not found in CSV file');
      }
      if (organizationData.orgType && !isOrgType(organizationData.orgType)) {
        throw new Error(`Organization Type must be one of: ${ORG_TYPES.map((t) => t.value).join(', ')}`);
      }
      if (!organizationData.contactEmail) {
        throw new Error('Contact email not found in CSV file');
      }
      if (userData.length === 0) {
        throw new Error('No user data found in CSV file');
      }
      
      // Validate user data
      for (const user of userData) {
        if (!user.name || !user.email) {
          throw new Error(`Invalid user data: ${user.name || user.email || 'Unknown user'} missing required fields`);
        }
        if (!user.email.includes('@')) {
          throw new Error(`Invalid email format: ${user.email}`);
        }
      }
      
      return {
        organization: organizationData,
        users: userData
      };
    } catch (parseError) {
      console.error('CSV parsing error:', parseError);
      throw parseError;
    }
  };

  const handlePreview = () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvText = e.target.result;
        const parsedData = parseCSV(csvText);
        
        if (!parsedData.organization.name) {
          toast.error('Could not find organization name in CSV file');
          return;
        }
        
        if (parsedData.users.length === 0) {
          toast.error('No valid user data found in CSV file');
          return;
        }
        
        setUploadData(parsedData);
        toast.success(`Parsed ${parsedData.users.length} users for organization "${parsedData.organization.name}"`);
      } catch (error) {
        console.error('Error parsing CSV:', error);
        toast.error('Error parsing CSV file. Please check the format.');
      }
    };
    
    reader.readAsText(selectedFile);
  };

  const handleProcess = () => {
    if (!uploadData) {
      toast.error('Please preview the data first');
      return;
    }
    
    if (!selectedRegion) {
      toast.error('Please select a region');
      return;
    }
    
    setProcessingStatus('Starting batch process...');
    setBatchResults(null);
    
    // Add selected region and registration settings to the data
    const batchDataWithRegion = {
      ...uploadData,
      selectedRegion: selectedRegion.value,
      defaultPassword,
      autoRegisterUsers
    };
    
    // Create a wrapper for the status callback that also captures results
    const statusCallbackWithResults = (status, results = null) => {
      setProcessingStatus(status);
      if (results) {
        setBatchResults(results);
        if (results.errors && results.errors.length > 0) {
          setShowResultsModal(true);
        }
      }
    };
    
    onBatchProcess(batchDataWithRegion, statusCallbackWithResults);
  };

  const handleReset = () => {
    setSelectedFile(null);
    setUploadData(null);
    setProcessingStatus('');
    setBatchResults(null);
    setShowResultsModal(false);
    setDefaultPassword('bdfhFUif7324');
    setAutoRegisterUsers(true);
    document.getElementById('csv-file-input').value = '';
  };

  // Modal component for showing batch results
  const ResultsModal = () => {
    if (!showResultsModal || !batchResults) return null;

    const { errors = [], summary = {}, users = [] } = batchResults;
    const warnings = errors.filter(error => error.startsWith('WARNING'));
    const failures = errors.filter(error => error.startsWith('FAILED'));
    const otherErrors = errors.filter(error => !error.startsWith('WARNING') && !error.startsWith('FAILED'));

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowResultsModal(false)}>
        <div className="bg-white rounded-lg shadow-xl max-w-4xl max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Batch Processing Results</h3>
            <button 
              onClick={() => setShowResultsModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
            {/* Summary */}
            {summary && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Summary</h4>
                <div className="text-sm text-blue-800 space-y-1">
                  <p><strong>Organization:</strong> {summary.organizationStatus}</p>
                  <p><strong>Region:</strong> {summary.currentRegion}</p>
                  <p><strong>Total Users:</strong> {summary.totalUsers}</p>
                  <p><strong>Successful:</strong> {summary.successfulUsers} ({summary.newUsers} new, {summary.existingUsers} existing)</p>
                  {summary.skippedUsers > 0 && <p><strong>Skipped:</strong> {summary.skippedUsers} (wrong region)</p>}
                  {summary.failedUsers > 0 && <p><strong>Failed:</strong> {summary.failedUsers}</p>}
                  {summary.autoRegisterUsers && summary.registrationSummary && (
                    <div className="mt-2 pt-2 border-t border-blue-200">
                      <p className="font-medium text-blue-900">Registration Summary:</p>
                      <p><strong>Registered:</strong> {summary.registrationSummary.registered}</p>
                      <p><strong>Already Registered:</strong> {summary.registrationSummary.alreadyRegistered}</p>
                      {summary.registrationSummary.registrationFailed > 0 && (
                        <p><strong>Registration Failed:</strong> {summary.registrationSummary.registrationFailed}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Warnings */}
            {warnings.length > 0 && (
              <div className="mb-6">
                <h4 className="font-medium text-yellow-800 mb-2 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Warnings ({warnings.length})
                </h4>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <ul className="text-sm text-yellow-800 space-y-1">
                    {warnings.map((warning, index) => (
                      <li key={index} className="break-words">{warning}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Failures */}
            {failures.length > 0 && (
              <div className="mb-6">
                <h4 className="font-medium text-red-800 mb-2 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  Failures ({failures.length})
                </h4>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <ul className="text-sm text-red-800 space-y-1">
                    {failures.map((failure, index) => (
                      <li key={index} className="break-words">{failure}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Other Errors */}
            {otherErrors.length > 0 && (
              <div className="mb-6">
                <h4 className="font-medium text-red-800 mb-2 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Errors ({otherErrors.length})
                </h4>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <ul className="text-sm text-red-800 space-y-1">
                    {otherErrors.map((error, index) => (
                      <li key={index} className="break-words">{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* User Details */}
            {users.length > 0 && (
              <div className="mb-4">
                <h4 className="font-medium text-gray-800 mb-2">User Processing Details</h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-60 overflow-y-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-300">
                        <th className="text-left py-1 px-2 font-medium text-gray-600">Name</th>
                        <th className="text-left py-1 px-2 font-medium text-gray-600">Email</th>
                        <th className="text-left py-1 px-2 font-medium text-gray-600">Status & Registration</th>
                        <th className="text-left py-1 px-2 font-medium text-gray-600">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user, index) => (
                        <tr key={index} className="border-b border-gray-200">
                          <td className="py-1 px-2 text-gray-700">{user.user}</td>
                          <td className="py-1 px-2 text-gray-700">{user.email}</td>
                          <td className="py-1 px-2">
                            <div className="space-y-1">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                user.success 
                                  ? user.isNew 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-blue-100 text-blue-800'
                                  : user.skipped 
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-red-100 text-red-800'
                              }`}>
                                {user.success 
                                  ? user.isNew ? 'Added' : 'Existing'
                                  : user.skipped ? 'Skipped' : 'Failed'
                                }
                              </span>
                              {user.registrationStatus && (
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-1 ${
                                  user.registrationStatus.wasRegistered 
                                    ? 'bg-green-100 text-green-800'
                                    : user.registrationStatus.alreadyRegistered
                                      ? 'bg-blue-100 text-blue-800'
                                      : user.registrationStatus.registrationFailed
                                        ? 'bg-red-100 text-red-800'
                                        : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {user.registrationStatus.wasRegistered 
                                    ? 'Registered'
                                    : user.registrationStatus.alreadyRegistered
                                      ? 'Already Registered'
                                      : user.registrationStatus.registrationFailed
                                        ? 'Reg. Failed'
                                        : 'No Registration'
                                  }
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-1 px-2 text-gray-700 text-xs">
                            <div className="space-y-1">
                              <div>{user.warning || user.error || 'Success'}</div>
                              {user.registrationStatus?.error && (
                                <div className="text-red-600">Reg Error: {user.registrationStatus.error}</div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={() => setShowResultsModal(false)}
              className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <h2 className="text-lg leading-6 font-medium text-gray-900 mb-4">Batch Organization & User Upload</h2>
        
        <div className="space-y-4">
          {/* File Selection */}
          <div>
            <label htmlFor="csv-file-input" className="block text-sm font-medium text-gray-700 mb-2">
              Select CSV File
            </label>
            <input
              id="csv-file-input"
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
              disabled={isProcessing}
            />
          </div>

          {/* Region Selection */}
          <div>
            <label htmlFor="region-select" className="block text-sm font-medium text-gray-700 mb-2">
              Current Region (Users will only be authorized for this region)
            </label>
            <Select
              id="region-select"
              value={selectedRegion}
              options={regionOptions}
              onChange={setSelectedRegion}
              isDisabled={isProcessing}
              className="text-sm"
              styles={{
                control: (provided) => ({
                  ...provided,
                  minHeight: '38px',
                }),
                menuPortal: base => ({ ...base, zIndex: 9999 })
              }}
              menuPortalTarget={document.body}
            />
            <p className="text-xs text-gray-500 mt-1">
              Only users with matching regions will be processed. Users with different regions will be skipped.
            </p>
          </div>

          {/* Auto Register Users Toggle */}
          <div>
            <div className="flex items-center">
              <input
                id="auto-register-checkbox"
                type="checkbox"
                checked={autoRegisterUsers}
                onChange={(e) => setAutoRegisterUsers(e.target.checked)}
                disabled={isProcessing}
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              />
              <label htmlFor="auto-register-checkbox" className="ml-2 block text-sm font-medium text-gray-700">
                Auto-register users after authorization
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              When enabled, users will be automatically registered with Firebase Auth and added to the database after being authorized.
            </p>
          </div>

          {/* Default Password Field */}
          {autoRegisterUsers && (
            <div>
              <label htmlFor="default-password" className="block text-sm font-medium text-gray-700 mb-2">
                Default Password for Auto-Registration
              </label>
              <input
                id="default-password"
                type="text"
                value={defaultPassword}
                onChange={(e) => setDefaultPassword(e.target.value)}
                disabled={isProcessing}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Enter default password for users"
              />
              <p className="text-xs text-gray-500 mt-1">
                This password will be used for all auto-registered users. Users can change it after their first login.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={handlePreview}
              disabled={!selectedFile || isProcessing}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400"
            >
              Preview Data
            </button>
            
            <button
              onClick={handleProcess}
              disabled={!uploadData || isProcessing}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400"
            >
              {isProcessing ? 'Processing...' : 'Process Batch'}
            </button>
            
            <button
              onClick={handleReset}
              disabled={isProcessing}
              className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-200"
            >
              Reset
            </button>

            {batchResults && (
              <button
                onClick={() => setShowResultsModal(true)}
                className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                View Results
              </button>
            )}
          </div>

          {/* Processing Status */}
          {processingStatus && (
            <div className={`mt-4 p-3 rounded-md ${
              processingStatus.includes('ERROR') 
                ? 'bg-red-50 border border-red-200' 
                : processingStatus.includes('WARNING') 
                  ? 'bg-yellow-50 border border-yellow-200'
                  : processingStatus.includes('completed')
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-blue-50 border border-blue-200'
            }`}>
              <p className={`text-sm ${
                processingStatus.includes('ERROR') 
                  ? 'text-red-700' 
                  : processingStatus.includes('WARNING') 
                    ? 'text-yellow-700'
                    : processingStatus.includes('completed')
                      ? 'text-green-700'
                      : 'text-blue-700'
              }`}>
                {processingStatus}
              </p>
            </div>
          )}

          {/* Data Preview */}
          {uploadData && (
            <div className="mt-6 space-y-4">
              <h3 className="text-md font-medium text-gray-700">Data Preview</h3>
              
              {/* Organization Preview */}
              <div className="bg-gray-50 p-3 rounded-md">
                <h4 className="text-sm font-medium text-gray-600 mb-2">Organization</h4>
                <div className="text-sm text-gray-700">
                  <p><strong>Name:</strong> {uploadData.organization.name}</p>
                  <p><strong>Contact:</strong> {uploadData.organization.contactName}</p>
                  <p><strong>Email:</strong> {uploadData.organization.contactEmail}</p>
                  <p><strong>Max Users:</strong> {uploadData.organization.maxUsers}</p>
                  <p><strong>Regions:</strong> {uploadData.organization.regions.join(', ')}</p>
                  <p><strong>Type:</strong> {orgTypeLabel(uploadData.organization.orgType || 'enterprise')}</p>
                </div>
              </div>

              {/* Users Preview */}
              <div className="bg-gray-50 p-3 rounded-md">
                <h4 className="text-sm font-medium text-gray-600 mb-2">Users ({uploadData.users.length})</h4>
                <div className="max-h-40 overflow-y-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-300">
                        <th className="text-left py-1 px-2 font-medium text-gray-600">Name</th>
                        <th className="text-left py-1 px-2 font-medium text-gray-600">Email</th>
                        <th className="text-left py-1 px-2 font-medium text-gray-600">Role</th>
                        <th className="text-left py-1 px-2 font-medium text-gray-600">Region</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadData.users.map((user, index) => (
                        <tr key={index} className="border-b border-gray-200">
                          <td className="py-1 px-2 text-gray-700">{user.name}</td>
                          <td className="py-1 px-2 text-gray-700">{user.email}</td>
                          <td className="py-1 px-2 text-gray-700">{user.role}</td>
                          <td className="py-1 px-2 text-gray-700">{user.region}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Format Instructions */}
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <h4 className="text-sm font-medium text-yellow-800 mb-2">CSV Format Requirements</h4>
            <div className="text-sm text-yellow-700">
              <p>The CSV should contain organization details at the top, followed by user data:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Company: [Organization Name]</li>
                <li>Start Date: [Date]</li>
                <li># of Total Seats: [Number]</li>
                <li>Point of Contact Name: [Name]</li>
                <li>Point of Contact Email: [Email]</li>
                <li>Point of Contact Phone: [Phone] (optional)</li>
                <li>Organization Type: enterprise | explorer | trial | internal | test (optional, default enterprise)</li>
                <li>Then a header row: Name, Email, Account Level, Region</li>
                <li>Account Level: 0 = trainee, 1 = team lead</li>
              </ul>
              {autoRegisterUsers && (
                <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded">
                  <p className="font-medium text-blue-800">Auto-Registration Enabled:</p>
                  <ul className="list-disc list-inside mt-1 space-y-1 text-blue-700">
                    <li>Users will be automatically registered with Firebase Auth</li>
                    <li>All users will use the default password: {defaultPassword ? '•'.repeat(defaultPassword.length) : 'Not set'}</li>
                    <li>Users must change their password after first login</li>
                    <li>Already registered users will be skipped</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Results Modal */}
      <ResultsModal />
    </div>
  );
};

export default BatchUpload;
